import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import IconButton from 'components/Buttons/IconButton'
import Skeleton from 'components/SkeletonLoader'

import { formatDatetime } from 'utils/datetime'
import { size } from 'utils/responsive'

import DateItem from './DateItem'
import { findDatesBetween, prepMarkedDates } from './helper'

type Props = {
  canClickNext?: boolean
  canClickPrevious?: boolean
  endDate: Date
  isLoading?: boolean
  markedDates?: Date[]
  onClickNext?: () => void
  onClickPrevious?: () => void
  onSelect?: (value: Date) => void
  startDate: Date
  value?: Date
}

const LinearDatePicker: React.FC<Props> = ({
  canClickNext = true,
  canClickPrevious = true,
  endDate,
  isLoading,
  onClickNext,
  onClickPrevious,
  onSelect,
  startDate,
  value,
  ...props
}) => {
  const isValid = endDate > startDate
  const [datesBetween, setDatesBetween] = useState<Date[]>()

  const markedDates = useMemo(() => (props.markedDates ? prepMarkedDates(props.markedDates) : []), [props.markedDates])

  useEffect(() => {
    if (isValid) {
      setDatesBetween(findDatesBetween(endDate, startDate))
    }
  }, [endDate, isValid, startDate])

  return (
    <Container>
      <NavigationIconButton
        disabled={!canClickPrevious || isLoading}
        icon="chevron-left"
        onClick={onClickPrevious}
        variant="ghost"
      />
      <DaysWrapper>
        {datesBetween && !isLoading && isValid ? (
          datesBetween.map((i, index) => {
            const formattedDate = formatDatetime(i, 'MM/dd/yyyy')

            return (
              <DateItem
                date={i}
                isMarked={markedDates?.includes(formattedDate)}
                isSelected={formatDatetime(value, 'MM/dd/yyyy') === formattedDate}
                key={index}
                onClick={() => onSelect?.(i)}
              />
            )
          })
        ) : (
          <Skeleton />
        )}
      </DaysWrapper>
      <NavigationIconButton
        disabled={!canClickNext || isLoading}
        icon="chevron-right"
        onClick={onClickNext}
        variant="ghost"
      />
    </Container>
  )
}

const Container = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 20px;
`

const DaysWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 1;
  gap: 12px;
  justify-content: space-around;
  overflow: hidden;
  padding: 0 4px;

  @media only screen and (max-width: ${size.medium}px) {
    gap: 2px;
  }
`

const NavigationIconButton = styled(IconButton)`
  flex: 0;
`

export default LinearDatePicker
